/**
 * Header V2 — JM26 cream/navy/gold redesign (september 2026)
 * Ét "Alle produkter"-mega-panel + AI-søgning (uændret backend-kontrakt)
 * Reuses backend: https://backend.jmdimensions.com/api/search/advanced
 * Se docs/HEADER_FOOTER_2026_09.md for arkitektur/beslutninger.
 */

(function () {
  'use strict';

  /* ─── Config ─── */
  const CONFIG = {
    apiUrl: 'https://backend.jmdimensions.com',
    endpoint: '/api/search/advanced',
    currency:
      typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : 'DKK',
    maxHistoryItems: 5,
    megaMenuDelay: 120,
    // 300ms/1 tegn loggede hver eneste as-you-type-keystroke som en søgning
    // (2026-09: 78% af alle logs var præfikser af en efterfølgende søgning,
    // "c"/"la"/"sko" toppede "mest søgte") — se JMD-backend's
    // lib/search-log.ts for kollaps-logikken på backend-siden af samme fix.
    searchDebounce: 350,
    minChars: 2,
  };

  const LABELS = (window.headerV2Settings && window.headerV2Settings.labels) || {};

  /* ─── State ─── */
  const state = {
    isSearchOpen: false,
    megaOpen: false,
    megaOpenedByHover: false,
    searchQuery: '',
    searchHistory: JSON.parse(localStorage.getItem('hv2SearchHistory') || '[]'),
    trendingSearches: (window.headerV2Settings && window.headerV2Settings.trendingSearches) || [],
    megaCloseTimer: null,
    mobileMenuOpen: false,
  };

  /* ─── DOM Elements ─── */
  const el = {};

  function cacheElements() {
    el.header = document.querySelector('.header-v2');
    el.searchWrap = document.querySelector('.hv2-search-wrap');
    el.searchInner = document.querySelector('.hv2-search-inner');
    el.searchInput = document.querySelector('[data-hv2-search-input]');
    el.searchDropdown = document.querySelector('[data-hv2-search-dropdown]');
    el.backdrop = document.querySelector('[data-hv2-backdrop]');
    el.animatedPlaceholder = document.querySelector('[data-hv2-animated-placeholder]');

    // Search views (desktop)
    el.defaultView = document.querySelector('[data-hv2-default-view]');
    el.searchView = document.querySelector('[data-hv2-search-view]');
    el.suggestionsContainer = document.querySelector('[data-hv2-suggestions]');
    el.ddHead = document.querySelector('[data-hv2-dd-head]');
    el.ddEyebrow = document.querySelector('[data-hv2-dd-eyebrow]');
    el.interpretation = document.querySelector('[data-hv2-interpretation]');
    el.resultsCount = document.querySelector('[data-hv2-results-count]');
    el.productGrid = document.querySelector('[data-hv2-product-grid]');
    el.seeAll = document.querySelector('[data-hv2-see-all]');

    // "Alle produkter"
    el.megaItem = document.querySelector('[data-hv2-mega-item]');
    el.megaToggle = document.querySelector('[data-hv2-mega-toggle]');
    el.megaPanel = document.querySelector('[data-hv2-mega]');

    // Mobile
    el.mobileOverlay = document.querySelector('[data-hv2-mobile-overlay]');
    el.mobileMenuToggle = document.querySelector('[data-hv2-menu-toggle]');
    el.mobileClose = document.querySelector('[data-hv2-mobile-close]');
    el.mobileSearchInput = document.querySelector('[data-hv2-mobile-search-input]');
    el.mobileResultsContainer = document.querySelector('[data-hv2-mobile-results]');
    el.mobileResultsCount = document.querySelector('[data-hv2-mobile-results-count]');
    el.mobileDefaultView = document.querySelector('[data-hv2-mobile-default]');
    el.mobileSearchView = document.querySelector('[data-hv2-mobile-search-view]');
    el.mobileDdHead = document.querySelector('[data-hv2-mobile-dd-head]');
    el.mobileDdEyebrow = document.querySelector('[data-hv2-mobile-dd-eyebrow]');
    el.mobileInterpretation = document.querySelector('[data-hv2-mobile-interpretation]');
    el.mobileSeeAll = document.querySelector('[data-hv2-mobile-see-all]');
    el.searchTrigger = document.querySelector('[data-hv2-search-trigger]');
  }

  /* ─── Utilities ─── */
  function escapeHtml(text) {
    if (!text) return '';
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  /* "Mobil" her betyder "viser burger/drawer-layoutet" (≤1279px), samme
     grænse som header-v2.css' konsoliderede responsive blok — ikke kun
     telefoner. Bruges kun til at gate ⌘K (som skal fokusere det skjulte
     desktop-søgefelt). */
  function isMobile() {
    return window.matchMedia('(max-width: 1279px)').matches;
  }

  /* ─── Search History ─── */
  function addToHistory(query) {
    if (!query || query.length < 2) return;
    state.searchHistory = state.searchHistory.filter(q => q.toLowerCase() !== query.toLowerCase());
    state.searchHistory.unshift(query);
    state.searchHistory = state.searchHistory.slice(0, CONFIG.maxHistoryItems);
    localStorage.setItem('hv2SearchHistory', JSON.stringify(state.searchHistory));
  }

  function clearHistory() {
    state.searchHistory = [];
    localStorage.removeItem('hv2SearchHistory');
    renderEmptyState();
  }

  window.hv2ClearHistory = clearHistory;

  /* ─── Normalize trending data (supports both string and {term,category} format) ─── */
  function normalizeTrending(items) {
    return items.map(item => {
      if (typeof item === 'string') return { term: item, category: '' };
      return { term: item.term || '', category: item.category || '' };
    }).filter(item => item.term);
  }

  /* ─── Empty-state: "Populære søgninger" + "Seneste søgninger" (JM26: plain rows, ingen piller/tal) ─── */
  function renderEmptyState() {
    if (!el.suggestionsContainer) return;

    let html = '';

    const trending = normalizeTrending(state.trendingSearches);
    if (trending.length > 0) {
      html += `
        <div class="hv2-dd-section">
          <p class="hv2-dd-section-title">${escapeHtml(LABELS.searchPopular || 'Populære søgninger')}</p>
          <div class="hv2-dd-list">
            ${trending.slice(0, 6).map(item => `
              <button type="button" class="hv2-dd-row" data-hv2-term="${escapeHtml(item.term)}">${escapeHtml(item.term)}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    if (state.searchHistory.length > 0) {
      html += `
        <div class="hv2-dd-section">
          <div class="hv2-dd-section-head">
            <p class="hv2-dd-section-title">${escapeHtml(LABELS.searchRecent || 'Seneste søgninger')}</p>
            <button type="button" class="hv2-dd-clear" onclick="window.hv2ClearHistory()">Ryd</button>
          </div>
          <div class="hv2-dd-list">
            ${state.searchHistory.map(term => `
              <button type="button" class="hv2-dd-row" data-hv2-term="${escapeHtml(term)}">${escapeHtml(term)}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    el.suggestionsContainer.innerHTML = html;

    el.suggestionsContainer.querySelectorAll('[data-hv2-term]').forEach(btn => {
      btn.addEventListener('click', () => {
        const term = btn.dataset.hv2Term;
        if (el.searchInput) {
          el.searchInput.value = term;
          el.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          el.searchInput.focus();
        }
      });
    });
  }

  /* ─── Session ID ─── */
  function getSessionId() {
    try {
      const stored = JSON.parse(localStorage.getItem('hv2_session') || 'null');
      if (stored && stored.id && Date.now() - stored.created < 30 * 60 * 1000) return stored.id;
    } catch (e) { /* ignore */ }
    const id = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('hv2_session', JSON.stringify({ id, created: Date.now() }));
    return id;
  }

  /* ─── AI Search API ─── */
  let searchController = null;

  function fetchSearchResults(query, signal) {
    return fetch(`${CONFIG.apiUrl}${CONFIG.endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        context: { session_id: getSessionId() },
        filters: { available_only: true },
      }),
      signal: signal,
    }).then(r => {
      if (!r.ok) throw new Error('Search API error: ' + r.status);
      return r.json();
    });
  }

  /* ─── Render the "Vi forstår det som" head (shared desktop/mobile) ─── */
  function renderDdHead(headEl, eyebrowEl, interpretationEl, interpretation, lowConfidence) {
    if (!headEl || !interpretationEl) return;
    if (!interpretation) {
      headEl.hidden = true;
      return;
    }
    if (eyebrowEl) {
      eyebrowEl.textContent = lowConfidence ? (LABELS.searchLowConf || 'Mente du') : (LABELS.searchInterpret || 'Vi forstår det som');
    }
    interpretationEl.textContent = interpretation;
    headEl.hidden = false;
  }

  /* Fælles fallback-kæde for "Vi forstår det som" (spec:
     meta.interpretation → conversational_suggestions[0] → selve
     søgeordet). Delt mellem desktop-dropdownen og mobil-overlayets
     søgning, som tidligere hver havde deres egen (lettere afvigende) kopi
     der kun faldt tilbage til suggestions[0] når low_confidence var sat —
     ellers blev linjen slet ikke vist. */
  function resolveInterpretation(meta, suggestions, query) {
    var interpretation = (meta && meta.interpretation) || '';
    var lowConfidence = !!(meta && meta.low_confidence);
    if (!interpretation && suggestions && suggestions.length > 0) {
      interpretation = suggestions[0];
    }
    if (!interpretation) {
      interpretation = query;
    }
    return { interpretation: interpretation, lowConfidence: lowConfidence };
  }

  function performSearch(query, container, countEl, headEl, eyebrowEl, interpretationEl) {
    if (!query || !container) return;

    container.innerHTML = renderListSkeleton(5);
    if (headEl) headEl.hidden = true;

    if (searchController) searchController.abort();
    searchController = new AbortController();

    fetchSearchResults(query, searchController.signal)
      .then(result => {
        const data = result.data || {};
        const products = data.products || [];
        const suggestions = data.conversational_suggestions || [];
        const meta = data.meta || {};
        const queryId = data.debug ? data.debug.query_id : null;
        const shown = products.slice(0, 5);
        const resultsWord = LABELS.searchResults || 'resultater';

        if (countEl) countEl.textContent = products.length + ' ' + resultsWord;
        updateSeeAll(products.length, query);

        if (products.length === 0) {
          if (headEl) headEl.hidden = true;
          const hint = suggestions.length > 0
            ? `<p style="margin-top:8px;font-size:0.9em;">Prøv i stedet: <strong>${escapeHtml(suggestions[0])}</strong></p>`
            : '';
          container.innerHTML = `<div class="hv2-search-message"><p>${escapeHtml(LABELS.searchEmpty || 'Ingen resultater – prøv et andet ord')}</p>${hint}</div>`;
          return;
        }

        const resolved = resolveInterpretation(meta, suggestions, query);
        renderDdHead(headEl, eyebrowEl, interpretationEl, resolved.interpretation, resolved.lowConfidence);

        container.innerHTML = shown.map((p, i) => renderProductListItem(p, i, queryId)).join('');
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        console.error('Search error:', err);
        if (headEl) headEl.hidden = true;
        container.innerHTML = '<div class="hv2-search-message"><p>Der skete en fejl. Prøv igen senere.</p></div>';
        updateSeeAll(0, '');
      });
  }

  /* ─── Product List Item (fælles for desktop-dropdown og mobil) ─── */
  /* API'en returnerer fuldstørrelses-CDN-URL'er — skaler til thumb-størrelse
     (48px visning → 96px @2x), ellers hentes 100-200 KB pr. thumb
     (PageSpeed-fund 2026-07-10). */
  function scaleShopifyImage(url, width) {
    if (!url || url.indexOf('cdn') === -1) return url;
    var clean = url.replace(/([?&])width=\d+(&?)/, function (m, pre, post) { return post ? pre : ''; });
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function renderProductListItem(product, index, queryId) {
    const handle = product.handle || product.product_id;
    const url = '/products/' + handle;
    const title = product.product_title || product.title;
    const image = scaleShopifyImage(product.image_url || product.featured_image || product.image || (product.images && product.images[0] && product.images[0].src) || (product.images && product.images[0]) || '', 96);
    const price = product.price || (product.variants && product.variants[0] ? product.variants[0].price : 0);
    const compareAtPrice = product.compare_at_price || (product.variants && product.variants[0] ? product.variants[0].compare_at_price : null);
    const productType = product.product_type || product.type || '';
    const pidStr = String(product.product_id || product.id);
    const qidStr = queryId ? String(queryId) : '';

    return `
      <a href="${url}" class="hv2-bestseller-item"
         data-track-product="true" data-product-id="${pidStr}" data-position="${index + 1}" data-query-id="${qidStr}">
        <img src="${image}" alt="${escapeHtml(title)}" class="hv2-bestseller-img" loading="lazy" width="48" height="48">
        <div class="hv2-bestseller-info">
          <span class="hv2-bestseller-name">${escapeHtml(title)}</span>
          ${productType ? `<div class="hv2-bestseller-meta">${escapeHtml(productType)}</div>` : ''}
        </div>
        <span class="hv2-bestseller-price">${formatPriceHtml(price, compareAtPrice)}</span>
      </a>`;
  }

  /* ─── List Skeleton Loader ─── */
  function renderListSkeleton(count) {
    let html = '';
    for (let i = 0; i < count; i++) {
      html += `
        <div class="hv2-bestseller-item" style="pointer-events:none;">
          <div class="hv2-bestseller-img" style="background:var(--hv2-skeleton);animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
          <div class="hv2-bestseller-info" style="flex:1;">
            <div style="height:13px;width:70%;background:var(--hv2-skeleton);border-radius:4px;margin-bottom:6px;animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
            <div style="height:11px;width:40%;background:var(--hv2-skeleton);border-radius:4px;animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
          </div>
          <div style="height:14px;width:50px;background:var(--hv2-skeleton);border-radius:4px;animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
        </div>`;
    }
    return html;
  }

  /* ─── Price Formatters ─── */
  function fmtPrice(p) {
    const n = parseFloat(p);
    try {
      return n.toLocaleString('da-DK', { style: 'currency', currency: CONFIG.currency, minimumFractionDigits: 2 });
    } catch (e) {
      return n.toLocaleString('da-DK', { minimumFractionDigits: 2 }) + ' kr';
    }
  }

  function formatPriceHtml(price, compareAtPrice) {
    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
      return `${fmtPrice(price)} <span class="price-compare">${fmtPrice(compareAtPrice)}</span>`;
    }
    return fmtPrice(price);
  }

  /* ─── Click Tracking ─── */
  /* `queryText` er valgfri — bruges af den native søgeresultatside (som
     aldrig sætter state.searchQuery, fordi den slet ikke går gennem
     AI-dropdownen) til stadig at sende søgeordet med til `search_term`. */
  function trackClick(productId, position, queryId, queryText) {
    const data = { query_id: queryId, product_id: productId, click_position: position, session_id: getSessionId(), query: queryText != null ? queryText : state.searchQuery };
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${CONFIG.apiUrl}/api/analytics/search-click`, new Blob([JSON.stringify(data)], { type: 'application/json' }));
    } else {
      fetch(`${CONFIG.apiUrl}/api/analytics/search-click`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data), keepalive: true }).catch(() => {});
    }
    stashPendingSearchAttribution(queryId);
  }

  document.addEventListener('click', e => {
    const link = e.target.closest('a[data-track-product]');
    if (link) trackClick(link.dataset.productId, parseInt(link.dataset.position, 10), link.dataset.queryId || null);
  });

  /* ─── Søgnings-konvertering (fase 3): kurv-attribut ved klik ───
     En klik-øjeblikkelig `fetch(cart/update.js)` risikerer at tabe
     kurv-cookien for en helt ny besøgende (skrivningen kan nå at blive
     afbrudt af den efterfølgende sidenavigation) — værdien parkeres derfor i
     localStorage og flushes i stedet ved NÆSTE sideindlæsning
     (flushPendingSearchAttribution, kaldt fra init()). Almindelig
     `__`-præfiks gør en kurv-attribut "privat" (ulæselig fra Liquid/Ajax-
     API'et) — bruger bevidst IKKE det her. */
  function stashPendingSearchAttribution(queryId) {
    if (!queryId) return; // intet at pege tilbage på uden et query_id
    try {
      localStorage.setItem('hv2_search_attr', JSON.stringify({
        v: `${getSessionId()}|${queryId}|${Date.now()}`,
        pending: true,
      }));
    } catch (e) { /* localStorage utilgængelig (privat browsing mv.) — spring attributionen over */ }
  }

  function flushPendingSearchAttribution() {
    if (!window.routes || !window.routes.cart_update_url) return;
    let stashed;
    try {
      stashed = JSON.parse(localStorage.getItem('hv2_search_attr') || 'null');
    } catch (e) { return; }
    if (!stashed || !stashed.pending || !stashed.v) return;

    // Fjern flaget FØR selve kaldet, ikke bagefter — en fejlet skrivning skal
    // ikke blive ved med at forsøge sig på hver eneste efterfølgende
    // sidevisning (en enkelt tabt tilskrivning er billigere end en evig retry-løkke).
    try {
      localStorage.setItem('hv2_search_attr', JSON.stringify({ v: stashed.v, pending: false }));
    } catch (e) { /* ignore */ }

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { jmd_search: stashed.v } }),
    }).catch(() => { /* stille — kunden mister intet synligt ved en fejlet baggrunds-skrivning */ });
  }

  /* ─── Søgnings-konvertering (fase 3): klik-tracking på den NATIVE
     søgeresultatside (/search?q=…, sections/main-search.liquid) ───
     Den side kører Shopifys egen søgning, ikke vores AI-endpoint — uden
     dette var ethvert klik herfra usynligt for søgeanalysen. Genbruger
     backend'ens 10-minutters-dedupe (lib/search-log.ts): kalder man
     `/api/search/advanced` igen for samme forespørgsel i samme session
     (fx fordi kunden allerede søgte i dropdownen først), oprettes der IKKE
     en ny logrække — kun et opslag der returnerer det eksisterende query_id. */
  function initNativeSearchTracking() {
    const grid = document.getElementById('product-grid');
    if (!grid) return; // ikke søgeresultatsiden
    if (grid.dataset.hv2Tracked) return; // init() kan køre flere gange (Theme Editor: shopify:section:load)
    grid.dataset.hv2Tracked = '1';
    const params = new URLSearchParams(window.location.search);
    const q = (params.get('q') || '').trim();
    if (!q) return;

    let nativeSearchQueryId = null;
    fetchSearchResults(q)
      .then(result => {
        const qid = result && result.data && result.data.debug ? result.data.debug.query_id : null;
        if (qid) nativeSearchQueryId = qid;
      })
      .catch(() => { /* stille — klik uden query_id er stadig bedre end slet intet */ });

    grid.addEventListener('click', e => {
      const link = e.target.closest('a[data-product-id]');
      if (!link || !grid.contains(link)) return;
      const cards = Array.from(grid.querySelectorAll('.product-item__wrapper'));
      const card = link.closest('.product-item__wrapper');
      const position = card ? cards.indexOf(card) + 1 : 0;
      trackClick(link.dataset.productId, position, nativeSearchQueryId, q);
    });
  }

  /* ─── Position helpers ─── */
  function getHeaderBottom() {
    if (el.header) return el.header.getBoundingClientRect().bottom;
    return 0;
  }

  function positionMega() {
    if (!el.megaPanel || !el.megaToggle) return;
    var btnRect = el.megaToggle.getBoundingClientRect();
    var bottom = getHeaderBottom();
    var maxW = (window.headerV2Settings && window.headerV2Settings.megaWidth) || 900;
    el.megaPanel.style.top = Math.max(bottom + 10, 0) + 'px';
    el.megaPanel.style.left = Math.max(btnRect.left, 16) + 'px';
    el.megaPanel.style.width = Math.min(maxW, window.innerWidth - btnRect.left - 16) + 'px';
  }

  function positionSearchDropdown() {
    if (!el.searchDropdown || !el.searchWrap) return;
    var rect = el.searchWrap.getBoundingClientRect();
    var bottom = getHeaderBottom();
    el.searchDropdown.style.top = Math.max(bottom + 10, 0) + 'px';
    el.searchDropdown.style.left = rect.left + 'px';
    // 560px (spec) fremfor "aldrig bredere end det (evt. sammenklemte)
    // søgefelt" — dropdownens egen bredde skal ikke arve feltets bredde,
    // kun dele dens venstrekant.
    el.searchDropdown.style.width = Math.min(560, window.innerWidth - rect.left - 16) + 'px';
  }

  /* ─── "Alle produkter" mega-panel ─── */
  function openMega() {
    if (!el.megaItem) return;
    closeSearch();
    el.megaItem.classList.add('hv2-mega-open');
    if (el.megaToggle) el.megaToggle.setAttribute('aria-expanded', 'true');
    positionMega();
    if (el.backdrop) el.backdrop.classList.add('active');
    state.megaOpen = true;
  }

  function closeMega() {
    if (!el.megaItem) return;
    el.megaItem.classList.remove('hv2-mega-open');
    if (el.megaToggle) el.megaToggle.setAttribute('aria-expanded', 'false');
    if (el.backdrop && !state.isSearchOpen) el.backdrop.classList.remove('active');
    state.megaOpen = false;
    state.megaOpenedByHover = false;
  }

  function initMegaMenu() {
    if (!el.megaItem || !el.megaToggle || !el.megaPanel) return;

    function scheduleClose() {
      state.megaCloseTimer = setTimeout(closeMega, CONFIG.megaMenuDelay);
    }
    function cancelClose() {
      if (state.megaCloseTimer) { clearTimeout(state.megaCloseTimer); state.megaCloseTimer = null; }
    }

    /* Pointer Events fremfor mouseenter/-leave så vi kan skelne en rigtig
       mus (hover åbner/lukker panelet) fra en berøring (som ikke bør
       trigge hover-adfærd). Uden det skelnede lukkede et almindeligt musik-
       klik ALTID panelet igen med det samme, fordi mouseenter allerede
       havde åbnet det INDEN klikket nåede frem — verificeret live: hover
       satte hv2-mega-open, det efterfølgende klik fjernede den igen i
       samme håndtering. state.megaOpenedByHover holder styr på om panelet
       p.t. er åbent PGA. hover, så et klik i samme hover-session ikke
       omgør det hover lige har gjort — luk sker i stedet ved museaway
       (mouseleave/scheduleClose), Esc eller klik udenfor. */
    var supportsPointer = typeof window.PointerEvent !== 'undefined';
    var enterEvt = supportsPointer ? 'pointerenter' : 'mouseenter';
    var leaveEvt = supportsPointer ? 'pointerleave' : 'mouseleave';

    el.megaItem.addEventListener(enterEvt, e => {
      if (supportsPointer && e.pointerType !== 'mouse') return;
      cancelClose();
      openMega();
      state.megaOpenedByHover = true;
    });
    el.megaItem.addEventListener(leaveEvt, e => {
      if (supportsPointer && e.pointerType !== 'mouse') return;
      scheduleClose();
    });
    el.megaToggle.addEventListener('focus', () => {
      // :focus-visible er ikke understøttet i alle browsere (matches()
      // kaster en SyntaxError for en ukendt pseudo-klasse, ikke false) —
      // fald tilbage til "åbn altid på focus" (den gamle adfærd) der.
      try {
        if (!el.megaToggle.matches(':focus-visible')) return;
      } catch (err) { /* browseren kender ikke :focus-visible — fortsæt */ }
      cancelClose();
      openMega();
    });

    el.megaToggle.addEventListener('click', e => {
      e.preventDefault();
      if (state.megaOpen) {
        if (state.megaOpenedByHover) return;
        closeMega();
      } else {
        openMega();
      }
    });

    /* Kategori-hover → skift fremhævet produkt-kort */
    const groups = el.megaPanel.querySelectorAll('[data-hv2-group]');
    const feats = el.megaPanel.querySelectorAll('[data-hv2-feat]');
    if (feats.length > 0) {
      groups.forEach(group => {
        group.addEventListener('mouseenter', () => {
          const key = group.getAttribute('data-hv2-group');
          let match = null;
          feats.forEach(f => { if (f.getAttribute('data-hv2-feat') === key) match = f; });
          if (match) {
            feats.forEach(f => { f.hidden = true; });
            match.hidden = false;
          }
        });
      });
    }
  }

  /* ─── Søgedropdown ─── */
  function openSearch() {
    if (!el.searchDropdown) return;
    closeMega();
    positionSearchDropdown();
    el.searchDropdown.classList.add('open');
    if (el.backdrop) el.backdrop.classList.add('active');
    state.isSearchOpen = true;
    showSearchView('default');
    renderEmptyState();
  }

  function closeSearch() {
    if (!el.searchDropdown) return;
    el.searchDropdown.classList.remove('open');
    if (el.backdrop && !state.megaOpen) el.backdrop.classList.remove('active');
    state.isSearchOpen = false;
    showSearchView('default');
  }

  function showSearchView(view) {
    if (el.defaultView) el.defaultView.style.display = view === 'default' ? '' : 'none';
    if (el.searchView) el.searchView.style.display = view === 'search' ? '' : 'none';
    if (view === 'default') updateSeeAll(0, '');
  }

  function syncHasValue() {
    if (!el.searchInner || !el.searchInput) return;
    el.searchInner.classList.toggle('hv2-has-value', el.searchInput.value.length > 0);
  }

  /* ─── Search Events ─── */
  let debounceTimer;

  function initSearchEvents() {
    if (!el.searchInput) return;

    // Klon inputtet for at fjerne eventuelle arvede theme-listeners.
    const clone = el.searchInput.cloneNode(true);
    el.searchInput.parentNode.replaceChild(clone, el.searchInput);
    el.searchInput = clone;

    el.searchInput.addEventListener('mousedown', e => e.stopPropagation());
    el.searchInput.addEventListener('focus', e => { e.stopPropagation(); openSearch(); });
    el.searchInput.addEventListener('click', e => { e.stopPropagation(); if (!state.isSearchOpen) openSearch(); });

    el.searchInput.addEventListener('input', e => {
      e.stopPropagation();
      syncHasValue();
      const query = e.target.value.trim();
      state.searchQuery = query;

      if (query.length >= CONFIG.minChars) {
        showSearchView('search');
        if (el.productGrid) el.productGrid.innerHTML = renderListSkeleton(5);

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          addToHistory(query);
          performSearch(query, el.productGrid, el.resultsCount, el.ddHead, el.ddEyebrow, el.interpretation);
        }, CONFIG.searchDebounce);
      } else {
        showSearchView('default');
        renderEmptyState();
      }
    });

    el.searchInput.addEventListener('keydown', e => {
      e.stopPropagation();
      if (!state.isSearchOpen) return;
      if (e.key === 'Escape') {
        closeSearch();
        el.searchInput.blur();
      }
    });
  }

  /* ─── Backdrop & Global Click ─── */
  function initGlobalEvents() {
    if (el.backdrop) {
      el.backdrop.addEventListener('click', e => {
        if (!e.target.closest('[data-hv2-search-dropdown]') && !e.target.closest('.hv2-search-wrap')) {
          closeSearch();
        }
        if (!e.target.closest('[data-hv2-mega]') && !e.target.closest('[data-hv2-mega-item]')) {
          closeMega();
        }
      });
    }

    document.addEventListener('click', e => {
      if (state.isSearchOpen && !e.target.closest('[data-hv2-search-dropdown]') && !e.target.closest('.hv2-search-wrap')) {
        closeSearch();
      }
      if (state.megaOpen && !e.target.closest('[data-hv2-mega]') && !e.target.closest('[data-hv2-mega-item]')) {
        closeMega();
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (state.isSearchOpen) closeSearch();
        if (state.megaOpen) closeMega();
        // Overlayets EGEN keydown-lytter (på søgefeltet) lukkede den kun
        // hvis fokus allerede sad i feltet — Esc gjorde intet resten af
        // tiden overlayet var åbent (fx med fokus på et menupunkt).
        if (state.mobileMenuOpen) closeMobileMenu();
      }

      // Cmd+K / Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isMobile() && el.searchInput) {
          el.searchInput.focus();
          openSearch();
        }
      }
    });

    // Hold panelerne positioneret ved scroll (sticky header flytter sig) og resize.
    let positionTicking = false;
    function repositionOpen() {
      if (state.isSearchOpen) positionSearchDropdown();
      if (state.megaOpen) positionMega();
    }
    window.addEventListener('scroll', () => {
      if (!positionTicking) {
        requestAnimationFrame(() => { repositionOpen(); positionTicking = false; });
        positionTicking = true;
      }
    }, { passive: true });
    window.addEventListener('resize', repositionOpen);

    // Luk mobil-drawer'en hvis vinduet vokser forbi burger-grænsen (fx en
    // tablet der roteres, eller devtools-panelet lukkes) — ellers bliver
    // den siddende åben oven på den nu-viste desktop-bar, med scroll
    // stadig låst.
    window.addEventListener('resize', () => {
      if (state.mobileMenuOpen && window.innerWidth >= 1280) closeMobileMenu();
    });
  }

  /* ─── Mobile Menu ─── */
  function openMobileMenu(searchMode) {
    if (!el.mobileOverlay) return;
    // Åbnet via søgetriggeren: vis KUN søgefeltet, ikke hele
    // kategori-accordionen/sekundær-links/knapperne — ejer-feedback:
    // "det er en dårlig oplevelse at rammes af hele menuen når man bare
    // vil søge". `.hv2-search-entry` skjuler resten af
    // [data-hv2-mobile-default] i CSS'en. Ryddes altid ved lukning
    // (closeMobileMenu) og ved åbning via selve hamburger-knappen, så en
    // tidligere søge-åbning aldrig "sidder fast" på næste menu-åbning.
    el.mobileOverlay.classList.toggle('hv2-search-entry', !!searchMode);
    el.mobileOverlay.classList.add('open');
    // Tving en synkron style-genberegning FØR noget forsøger at fokusere et
    // element inde i overlayet: uden dette kan et focus()-kald i samme
    // JS-tick stadig ramme den gamle (endnu ikke opdaterede)
    // visibility:hidden-tilstand og fejle stille — verificeret live
    // (tastaturet åbnede ikke ved tryk på søgetriggeren).
    void el.mobileOverlay.offsetHeight;
    if (el.mobileMenuToggle) el.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    // overflow låses på BÅDE <html> og <body>: html har overflow-x:clip
    // (se toppen af header-v2.css), som gør body's egen overflow:hidden
    // virkningsløst for at stoppe siden bag overlayet i at scrolle.
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    state.mobileMenuOpen = true;
  }

  function closeMobileMenu() {
    if (!el.mobileOverlay) return;
    el.mobileOverlay.classList.remove('open');
    el.mobileOverlay.classList.remove('hv2-search-entry');
    if (el.mobileMenuToggle) el.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    state.mobileMenuOpen = false;
  }

  function initMobileMenu() {
    if (el.mobileMenuToggle) {
      el.mobileMenuToggle.addEventListener('click', e => {
        e.preventDefault();
        openMobileMenu();
      });
    }

    if (el.mobileClose) {
      el.mobileClose.addEventListener('click', e => {
        e.preventDefault();
        closeMobileMenu();
      });
    }

    // Luk overlayet når kurv-CTA'en i bunden åbner minicart-drawer'en (samme klik).
    if (el.mobileOverlay) {
      const cartCta = el.mobileOverlay.querySelector('.hv2-btn.hv2-btn--navy');
      if (cartCta) {
        cartCta.addEventListener('click', () => closeMobileMenu());
      }
    }

    // Accordion toggle
    document.querySelectorAll('.hv2-mobile-item[data-has-children]').forEach(item => {
      const btn = item.querySelector('.hv2-mobile-btn');
      const submenu = item.querySelector('.hv2-mobile-sub');
      if (!btn || !submenu) return;

      btn.addEventListener('click', e => {
        e.preventDefault();
        const isExpanded = item.classList.contains('expanded');
        document.querySelectorAll('.hv2-mobile-item.expanded').forEach(other => {
          other.classList.remove('expanded');
          const sub = other.querySelector('.hv2-mobile-sub');
          if (sub) sub.classList.remove('open');
          const otherBtn = other.querySelector('.hv2-mobile-btn');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        });
        if (!isExpanded) {
          item.classList.add('expanded');
          submenu.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Mobil søgning — deler `performSearch` (og dermed dens
    // AbortController) med desktop-dropdownen i stedet for sin egen
    // duplikerede fetch/render-kopi. Kun ét af de to overlays kan være synligt
    // ad gangen, så delt annullering er ikke et problem.
    let mobileDebounce;

    if (el.mobileSearchInput) {
      el.mobileSearchInput.addEventListener('input', e => {
        const query = e.target.value.trim();
        state.searchQuery = query;

        if (query.length >= CONFIG.minChars) {
          if (el.mobileDefaultView) el.mobileDefaultView.style.display = 'none';
          if (el.mobileSearchView) el.mobileSearchView.style.display = '';
          if (el.mobileResultsContainer) el.mobileResultsContainer.innerHTML = renderListSkeleton(4);
          if (el.mobileDdHead) el.mobileDdHead.hidden = true;

          clearTimeout(mobileDebounce);
          mobileDebounce = setTimeout(() => {
            addToHistory(query);
            performSearch(query, el.mobileResultsContainer, el.mobileResultsCount, el.mobileDdHead, el.mobileDdEyebrow, el.mobileInterpretation);
          }, CONFIG.searchDebounce);
        } else {
          if (el.mobileDefaultView) el.mobileDefaultView.style.display = '';
          if (el.mobileSearchView) el.mobileSearchView.style.display = 'none';
        }
      });

      el.mobileSearchInput.addEventListener('keydown', e => {
        if (e.key === 'Escape') closeMobileMenu();
        if (e.key === 'Enter') {
          const q = el.mobileSearchInput.value.trim();
          if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
        }
      });
    }
  }

  /* Mobil søge-trigger (fake felt under hovedrækken) → åbn overlay + fokusér søgefelt */
  function initMobileTrigger() {
    if (!el.searchTrigger) return;
    el.searchTrigger.addEventListener('click', e => {
      e.preventDefault();
      openMobileMenu(true);
      if (!el.mobileSearchInput) return;
      // To modstridende krav, begge målt/dokumenterede, samme kald begge
      // veje:
      // 1) iOS Safari åbner KUN det bløde tastatur hvis focus() sker
      //    SYNKRONT inde i selve klik-handleren — venter man bare én
      //    microtask/rAF, er "user activation"-vinduet for browserens
      //    tastatur-heuristik ofte allerede lukket, selvom
      //    document.activeElement fint bliver feltet.
      // 2) Chrome accepterede IKKE det synkrone kald alene (verificeret
      //    live, selv efter openMobileMenu()'s egen offsetHeight-reflow) —
      //    kun et kald EFTER mindst ét fuldført maleri (dobbelt
      //    requestAnimationFrame) fik fokus til rent faktisk at flytte sig
      //    væk fra søgetrigger-knappen.
      // Løsning: kald focus() begge steder. Et andet kald på et allerede
      // fokuseret element er et no-op, så det er harmløst at "ramme" i
      // begge browsere.
      el.mobileSearchInput.focus();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.mobileSearchInput.focus();
        });
      });
    });
  }

  /* ─── Sticky (egen implementering, se header-v2.css/.liquid for hvorfor
     <header> altid bærer data-sticky="none" mod theme.js) ─── */
  function initStickySystem() {
    if (!el.header) return;
    const type = el.header.getAttribute('data-hv2-sticky');
    let lastY = window.scrollY;
    let ticking = false;

    function update() {
      const y = window.scrollY;
      el.header.classList.toggle('scrolled', y > 10);
      if (type === 'on-scroll-up') {
        if (y > lastY && y > 120) {
          el.header.classList.add('hv2-hide');
        } else {
          el.header.classList.remove('hv2-hide');
        }
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ─── Animated Placeholder ─── */
  const placeholders = (window.headerV2Settings && window.headerV2Settings.animatedPlaceholders) || ['Søg efter produkter...'];
  const placeholderInterval = ((window.headerV2Settings && window.headerV2Settings.placeholderInterval) || 4) * 1000;
  let currentPHIndex = 0;
  let phAnimInterval = null;
  let isAnimating = false;
  let animTimeout = null;
  /* run-token gør animationerne single-flight (samme fix som juli-udgaven). */
  let animRunId = 0;

  function typeText(element, text, cb) {
    if (!element || !text) return;
    const run = ++animRunId;
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = true;
    let i = 0;
    element.textContent = '';
    (function type() {
      if (run !== animRunId) return;
      if (i < text.length) {
        element.textContent += text.charAt(i);
        i++;
        animTimeout = setTimeout(type, 40);
      } else {
        animTimeout = null;
        isAnimating = false;
        if (cb) cb();
      }
    })();
  }

  function deleteText(element, cb) {
    if (!element) return;
    const run = ++animRunId;
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = true;
    let text = element.textContent;
    (function del() {
      if (run !== animRunId) return;
      if (text.length > 0) {
        text = text.slice(0, -1);
        element.textContent = text;
        animTimeout = setTimeout(del, 25);
      } else {
        animTimeout = null;
        isAnimating = false;
        if (cb) cb();
      }
    })();
  }

  function cyclePlaceholder() {
    if (!el.animatedPlaceholder || placeholders.length === 0) return;
    if (el.searchInput && el.searchInput.value.length > 0) return;
    if (isAnimating) return;

    deleteText(el.animatedPlaceholder, () => {
      currentPHIndex = (currentPHIndex + 1) % placeholders.length;
      typeText(el.animatedPlaceholder, placeholders[currentPHIndex]);
    });
  }

  function startPHAnimation() {
    if (phAnimInterval || placeholders.length <= 1) return;
    phAnimInterval = setInterval(cyclePlaceholder, placeholderInterval);
  }

  function stopPHAnimation() {
    animRunId++;
    if (phAnimInterval) { clearInterval(phAnimInterval); phAnimInterval = null; }
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = false;
  }

  function initAnimatedPlaceholder() {
    if (!el.animatedPlaceholder || placeholders.length === 0) return;
    typeText(el.animatedPlaceholder, placeholders[0], () => {
      setTimeout(startPHAnimation, placeholderInterval);
    });

    if (el.searchInput) {
      const toggle = () => {
        if (el.searchInput.value.length > 0) {
          el.animatedPlaceholder.style.display = 'none';
          stopPHAnimation();
        } else {
          el.animatedPlaceholder.style.display = '';
          startPHAnimation();
        }
      };
      el.searchInput.addEventListener('input', toggle);
      el.searchInput.addEventListener('change', toggle);
      toggle();
    }
  }

  /* ─── Trending Searches (API) ─── */
  function fetchTrending() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('hv2_trending') || 'null');
      if (cached && cached.terms && cached.terms.length > 0 && Date.now() - cached.ts < 300000) {
        state.trendingSearches = cached.terms;
        return;
      }
    } catch (e) { /* ignore */ }

    fetch(`${CONFIG.apiUrl}/api/analytics/trending-searches`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(result => {
        const terms = result.data;
        if (Array.isArray(terms) && terms.length > 0) {
          state.trendingSearches = terms;
          sessionStorage.setItem('hv2_trending', JSON.stringify({ terms, ts: Date.now() }));
        }
      })
      .catch(() => { /* behold fallback fra Trending Keyword-blokkene */ });
  }

  /* ─── Cart Badge Observer ─── */
  function initCartBadge() {
    const badge = document.querySelector('.hv2-cart-badge');
    if (!badge) return;

    function updateVisibility() {
      const count = parseInt(badge.textContent, 10);
      badge.style.display = count > 0 ? '' : 'none';
    }

    updateVisibility();

    const observer = new MutationObserver(updateVisibility);
    observer.observe(badge, { childList: true, characterData: true, subtree: true });
  }

  /* ─── Fix --height-header (theme.js sætter den før CSS'en er landet) ─── */
  function fixHeaderHeightVar() {
    if (!el.header) return;
    function apply() {
      var h = el.header.offsetHeight;
      if (h > 0 && h < 500) {
        document.body.style.setProperty('--height-header', h + 'px');
      }
    }
    apply();
    window.addEventListener('load', apply);
  }

  /* "Se alle N resultater →" i søge-dropdownen — desktop og mobil hver sit mål. */
  function updateSeeAll(count, query) {
    const label = LABELS.searchSeeAll || 'Se alle';
    // Spec: antallet står i "N resultater" (results-count-elementet ved
    // siden af); "Se alle →" gentog det tidligere ("Se alle N resultater
    // →"), så tallet stod to gange i samme fod.
    [el.seeAll, el.mobileSeeAll].forEach(seeAll => {
      if (!seeAll) return;
      if (count > 0 && query) {
        seeAll.textContent = label + ' →';
        seeAll.href = '/search?q=' + encodeURIComponent(query);
        seeAll.hidden = false;
      } else {
        seeAll.hidden = true;
      }
    });
  }

  /* Ryd-knap + Enter/SØG-navigation. SKAL kaldes EFTER initSearchEvents
     (cloneNode-hacket udskifter inputtet). */
  function initSearchExtras() {
    if (!el.searchInput) return;

    const clearBtn = document.querySelector('[data-hv2-search-clear]');
    if (clearBtn) {
      const syncClear = () => { clearBtn.hidden = !(el.searchInput.value.length > 0); };
      el.searchInput.addEventListener('input', syncClear);
      syncClear();
      clearBtn.addEventListener('click', e => {
        e.stopPropagation();
        el.searchInput.value = '';
        el.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        el.searchInput.focus();
      });
    }

    el.searchInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        const q = el.searchInput.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
      }
    });

    const searchBtn = document.querySelector('.hv2-search-btn');
    if (searchBtn) {
      searchBtn.addEventListener('click', e => {
        e.stopPropagation();
        const q = el.searchInput.value.trim();
        if (q) {
          window.location.href = '/search?q=' + encodeURIComponent(q);
        } else {
          el.searchInput.focus();
        }
      });
    }
  }

  function init() {
    cacheElements();
    fixHeaderHeightVar();
    initSearchEvents();
    initGlobalEvents();
    initMegaMenu();
    initMobileMenu();
    initStickySystem();
    initAnimatedPlaceholder();
    initCartBadge();
    fetchTrending();
    /* efter initSearchEvents (input er klonet dér) */
    initSearchExtras();
    initMobileTrigger();
    flushPendingSearchAttribution();
    initNativeSearchTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Editor support
  document.addEventListener('shopify:section:load', e => {
    if (e.target.querySelector('.header-v2')) init();
  });
})();
